import { addZeroesAndSeparatevalue } from '@utils/formatting-utils';

import { isEmpty } from 'lodash';
import DateService from 'src/core/services/DateService';
import XLSX from 'xlsx-js-style';

type HeaderDef = {
  label: string;
  visible: boolean;
  isDate: boolean;
  isCurrency: boolean;
  currencySymbol?: string;
};

export type GroupHeader = {
  title: string;
  fromKey: string;
  toKey: string;
};

export type ExcelStyles = {
  groupHeader?: any;
  header?: any;
  cell?: any;
};

interface DownloadExcelProps {
  data: any[];
  filename: string;
  headers: Record<string, HeaderDef>;
  specialHeaders?: { [label: string]: string[] };
  sheetName?: string;
  groupHeaders?: GroupHeader[];
  styles?: ExcelStyles;
  freezeHeaderRows?: number;
  columnWidths?: number[];
  headerRowHeight?: number;
  groupHeaderRowHeight?: number;
  separator?: {
    enabled?: boolean;
    colorRgb?: string;
    style?: 'thin' | 'medium' | 'thick';
    applyTo?: 'headerOnly' | 'allRows';
  };
}

interface DownloadMultiSheetExcelProps {
  filename: string;
  sheets: {
    data: any[];
    headers: Record<string, HeaderDef>;
    specialHeaders?: { [label: string]: string[] };
    sheetName?: string;
    groupHeaders?: GroupHeader[];
    styles?: ExcelStyles;
    freezeHeaderRows?: number;
    columnWidths?: number[];
    headerRowHeight?: number;
    groupHeaderRowHeight?: number;
    separator?: {
      enabled?: boolean;
      colorRgb?: string;
      style?: 'thin' | 'medium' | 'thick';
      applyTo?: 'headerOnly' | 'allRows';
    };
  }[];
}

export function downloadExcelFileMultiSheet({
  filename,
  sheets
}: DownloadMultiSheetExcelProps) {
  if (isEmpty(filename)) return;
  if (!sheets || sheets.length === 0) return;

  const formatDate = (dateString: string) => {
    if (isEmpty(dateString)) return '';

    return DateService.formatDate(dateString);
  };

  const getFieldValue = (
    item: any,
    field: string,
    isDate?: boolean,
    isCurrency?: boolean,
    currencySymbol?: string
  ) => {
    const value = item?.[field];
    if (value && typeof value === 'object' && 'Title' in value)
      return value.Title;

    if (isDate) return formatDate(value);

    if (isCurrency) {
      const v = Number(value);
      let displayValue = `${addZeroesAndSeparatevalue(Math.abs(v))}`;
      if (currencySymbol) displayValue = currencySymbol + ' ' + displayValue;

      return displayValue;
    }

    return value ?? '';
  };

  const wb = XLSX.utils.book_new();

  sheets.forEach((sheet) => {
    const {
      data,
      headers,
      specialHeaders,
      sheetName = 'Sheet1',
      groupHeaders = [],
      styles,
      freezeHeaderRows,
      columnWidths,
      headerRowHeight = 14,
      groupHeaderRowHeight = 20,
      separator = {
        enabled: false,
        colorRgb: 'FF1F4E79',
        style: 'medium',
        applyTo: 'headerOnly'
      }
    } = sheet;

    if (!data || data.length === 0) return;

    const visibleKeys = Object.keys(headers).filter((k) => headers[k]?.visible);
    const headerLabels = visibleKeys.map((k) => headers[k].label);

    const specialEntries = specialHeaders ? Object.entries(specialHeaders) : [];
    const allHeaderLabels = [
      ...headerLabels,
      ...specialEntries.map(([label]) => label)
    ];
    const totalCols = allHeaderLabels.length;

    const rows = data.map((item: any) => {
      const base = visibleKeys.map((k) =>
        getFieldValue(
          item,
          k,
          headers[k]?.isDate,
          headers[k]?.isCurrency,
          headers[k]?.currencySymbol
        )
      );

      const extra = specialEntries.map(([, fields]) =>
        fields.map((f: string) => getFieldValue(item, f)).join(' ')
      );

      return [...base, ...extra];
    });

    const hasGroups = groupHeaders.length > 0;
    const groupRow = hasGroups ? new Array(totalCols).fill('') : null;

    const aoa: any[][] = hasGroups
      ? [groupRow as string[], allHeaderLabels, ...rows]
      : [allHeaderLabels, ...rows];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Styles
    const defaultGroupHeaderStyle = {
      font: { sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'FF1F4E79' } },
      alignment: { horizontal: 'center', vertical: 'center' }
    };
    const defaultHeaderStyle = {
      font: { sz: 11, bold: true, color: { rgb: 'FF000000' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFD9E1F2' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
    };
    const defaultCellStyle = {
      alignment: { vertical: 'center', wrapText: true }
    };

    const groupHeaderStyle = styles?.groupHeader ?? defaultGroupHeaderStyle;
    const headerStyle = styles?.header ?? defaultHeaderStyle;
    const cellStyle = styles?.cell ?? defaultCellStyle;

    const applyStyle = (r: number, c: number, styleObj: any) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { t: 's', v: '' };
      ws[addr].s = { ...(ws[addr].s || {}), ...styleObj };
    };

    const addLeftBorder = (baseStyle: any) => ({
      ...baseStyle,
      border: {
        ...(baseStyle?.border ?? {}),
        left: {
          style: separator.style ?? 'medium',
          color: { rgb: separator.colorRgb ?? 'FF1F4E79' }
        }
      }
    });

    const headerRowIndex = hasGroups ? 1 : 0;
    const dataStartRowIndex = hasGroups ? 2 : 1;

    for (let c = 0; c < totalCols; c++)
      applyStyle(headerRowIndex, c, headerStyle);

    for (let r = dataStartRowIndex; r < aoa.length; r++) {
      for (let c = 0; c < totalCols; c++) applyStyle(r, c, cellStyle);
    }

    if (hasGroups) {
      const merges: XLSX.Range[] = [];
      const keyToCol = (key: string) => visibleKeys.indexOf(key);

      groupHeaders.forEach(
        (g: { fromKey: string; toKey: string; title: any }) => {
          const startCol = keyToCol(g.fromKey);
          const endCol = keyToCol(g.toKey);
          if (startCol === -1 || endCol === -1) return;

          const s = Math.min(startCol, endCol);
          const e = Math.max(startCol, endCol);

          ws[XLSX.utils.encode_cell({ r: 0, c: s })] = { t: 's', v: g.title };
          merges.push({ s: { r: 0, c: s }, e: { r: 0, c: e } });

          for (let c = s; c <= e; c++) applyStyle(0, c, groupHeaderStyle);
        }
      );

      ws['!merges'] = merges;
    }

    if (separator.enabled && hasGroups) {
      const keyToCol = (key: string) => visibleKeys.indexOf(key);

      groupHeaders.forEach((g: { fromKey: string }) => {
        const startCol = keyToCol(g.fromKey);
        if (startCol <= 0) return;

        if (separator.applyTo === 'allRows') {
          for (let r = headerRowIndex; r < aoa.length; r++) {
            const s = r === headerRowIndex ? headerStyle : cellStyle;
            applyStyle(r, startCol, addLeftBorder(s));
          }
        } else {
          applyStyle(headerRowIndex, startCol, addLeftBorder(headerStyle));
        }
      });
    }

    ws['!rows'] = hasGroups
      ? [{ hpt: groupHeaderRowHeight }, { hpt: headerRowHeight }]
      : [{ hpt: headerRowHeight }];

    if (columnWidths?.length === totalCols) {
      ws['!cols'] = columnWidths.map((w: any) => ({ wch: w }));
    } else {
      const MAX_WIDTH = 30;
      ws['!cols'] = allHeaderLabels.map((label, idx) => {
        const maxHeaderLength = String(label ?? '').length;
        const maxContentLength = Math.max(
          0,
          ...rows.map((r: any[]) => (r?.[idx] ? String(r[idx]).length : 0))
        );
        const calculatedWidth = Math.max(maxHeaderLength, maxContentLength) + 2;

        return { wch: Math.min(calculatedWidth, MAX_WIDTH) };
      });
    }

    const freezeRows =
      typeof freezeHeaderRows === 'number'
        ? freezeHeaderRows
        : hasGroups
          ? 2
          : 1;
    (ws as any)['!freeze'] = { xSplit: 0, ySplit: freezeRows };

    // Append the worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  // Write workbook
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.xlsx`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
